import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ResponsiveContainer,
  ResponsiveContent,
  ResponsiveHeader,
  ResponsiveFooter,
} from '../ui/ResponsiveLayout';

const Frame132: React.FC = () => {
  const navigate = useNavigate();

  const handleNext = () => {
    navigate('/frame-133');
  };

  const handleBack = () => {
    navigate('/frame-140');
  };

  return (
    <ResponsiveContainer background="stone">
      <ResponsiveContent>
        <ResponsiveHeader title="Set Up Your Plan" />

        <div className="max-w-4xl space-y-8">
          {/* Introduction */}
            <div className="space-y-4">
            <p className="text-stone-800 text-lg font-medium font-['Work_Sans'] leading-relaxed">
                Every estate plan starts with a foundation, and this step helps you put that foundation in place.
                We’ll ask for some basic information about you and the people you trust. This helps personalize
                your plan, keep everything organized, and ensure the right people can step in if they ever need to.
                You’re always in control of what you share, and you can update or revisit this information at any time.
            </p>
            </div>

            {/* What You'll Do */}
            <div className="bg-white rounded-lg border border-neutral-200 p-6">
            <h2 className="text-xl font-semibold text-cyan-900 mb-4">What You’ll Do</h2>
            <div className="space-y-4">
                <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-cyan-700 text-white flex items-center justify-center font-semibold">
                    1
                </div>
                <div>
                    <h3 className="font-semibold text-stone-800 mb-1">
                    Enter Basic Personal Details
                    </h3>
                    <p className="text-stone-600">
                    Add key information like your birthdate, contact details, and address so your
                    plan is personalized and easier to manage.
                    </p>
                </div>
                </div>

                <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-cyan-700 text-white flex items-center justify-center font-semibold">
                    2
                </div>
                <div>
                    <h3 className="font-semibold text-stone-800 mb-1">
                    Add or Sync Trusted Contacts
                    </h3>
                    <p className="text-stone-600">
                    Include the people you trust, like family members, decision-makers, or executors,
                    so the right people are connected to your plan.
                    </p>
                </div>
                </div>

                <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-cyan-700 text-white flex items-center justify-center font-semibold">
                    3
                </div>
                <div>
                    <h3 className="font-semibold text-stone-800 mb-1">
                    Document Account Access Instructions
                    </h3>
                    <p className="text-stone-600">
                      Record where your login details are stored and how your executor can access them when needed.
                    </p>
                </div>
                </div>
            </div>

            <p className="text-stone-600 text-sm mt-5 italic">
                You can complete this at your own pace. Nothing here has to be perfect.
            </p>
            </div>

          {/* Why This Matters */}
          <div className="bg-cyan-50 border border-cyan-100 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-cyan-900 mb-3">Why This Matters</h2>
            <ul className="space-y-2 text-stone-700">
              <li className="flex items-start gap-2">
                <span className="text-cyan-700 mt-1">✓</span>
                <span>Creates a secure foundation for all other planning workflows</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-cyan-700 mt-1">✓</span>
                <span>Helps ensure your information is accurate and accessible when needed</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-cyan-700 mt-1">✓</span>
                <span>Makes it easier for loved ones or executors to step in later</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-cyan-700 mt-1">✓</span>
                <span>Reduces friction and guesswork across the rest of your plan</span>
              </li>
            </ul>
          </div>

          {/* Estimated Time */}
          <div className="text-center text-stone-600">
            <p className="text-sm">
              <span className="font-semibold">Estimated time:</span> ~5–10 minutes
            </p>
          </div>

          {/* Privacy & Security */}
          <div className="bg-white rounded-lg border border-stone-200 p-6">
            <h2 className="text-lg font-semibold text-cyan-900 mb-3">Privacy &amp; Security</h2>
            <p className="text-stone-700 leading-relaxed">
              Your information is encrypted and stored securely. You decide who has access, and
              nothing is shared unless you explicitly allow it.
            </p>
          </div>
        </div>
      </ResponsiveContent>

      <ResponsiveFooter
        title="Setup Your Plan"
        subtitle="Set the basics before you begin"
        progressPercent={0}
        onNext={handleNext}
        onBack={handleBack}
        nextText="Get Started"
        backText="Back to Dashboard"
      />
    </ResponsiveContainer>
  );
};

export default Frame132;